import type { PseoStat, FaqItem } from './content';

// ─── Comparison content ───────────────────────────────────────────────────────
// Comparative claims must be accurate and verifiable. Competitor prices are public list
// prices checked in October 2026 (see PRICES_CHECKED). Re-check before each update and
// never state a competitor limitation we have not verified on their own site.

export const PRICES_CHECKED = 'October 2026';

export interface ComparisonRow {
  feature: string;
  minimoes: string;
  competitor: string;
  winner: 'minimoes' | 'tie' | 'competitor';
}

export interface CompetitorContent {
  headline: string;
  subheadline: string;
  switchReason: string;
  comparisonRows: ComparisonRow[];
  competitorWeaknesses: string[];
  minimoesStrengths: string[];
  stats: PseoStat[];
  faqs: FaqItem[];
  ctaHeadline: string;
  ctaBody: string;
}

export const competitorContent: Record<string, CompetitorContent> = {
  // ─── Text chatbots: the replacement market ──────────────────────────────────

  intercom: {
    headline: 'Minimoes vs Intercom Fin: A Mascot That Talks vs a Bubble That Types',
    subheadline:
      'Intercom Fin is a strong text support agent with per-resolution pricing. Minimoes gives your AI a 3D face and a voice, with flat monthly plans and a free tier.',
    switchReason:
      `Intercom charges per seat plus $0.99 per Fin outcome (public pricing, ${PRICES_CHECKED}), so the bill grows with every resolved conversation. Minimoes uses flat monthly plans with included credits. More importantly, it changes how visitors experience your AI: a talking character greets them, listens when they speak and answers out loud. Choose Minimoes when you want your AI to be noticed and remembered, not just answer tickets.`,
    comparisonRows: [
      { feature: 'Visual presence', minimoes: '3D mascot with voice', competitor: 'Text chat bubble', winner: 'minimoes' },
      { feature: 'Voice conversation', minimoes: 'Speaks & listens', competitor: 'Text in the web widget', winner: 'minimoes' },
      { feature: 'Pricing model', minimoes: 'Flat monthly plans', competitor: 'Per seat + $0.99 per outcome', winner: 'minimoes' },
      { feature: 'Free plan', minimoes: 'Yes', competitor: 'Free trial', winner: 'minimoes' },
      { feature: 'Full helpdesk & inbox', minimoes: 'Escalates to your helpdesk', competitor: 'Built-in', winner: 'competitor' },
      { feature: 'Livestream overlay', minimoes: 'OBS: Twitch, YouTube, Kick', competitor: 'No', winner: 'minimoes' },
    ],
    competitorWeaknesses: [
      'Per-outcome pricing makes costs grow with every resolved conversation',
      'A text bubble looks like every other website',
      'Seat-based plans add up as the team grows',
    ],
    minimoesStrengths: [
      'A branded 3D mascot with a preset or cloned voice',
      'Flat plans with included credits and a free tier',
      'Escalates to Intercom itself if you keep it as your helpdesk',
    ],
    stats: [
      { value: '$0', label: 'To start' },
      { value: 'Flat', label: 'Monthly pricing' },
      { value: '3D', label: 'Mascot with a voice' },
    ],
    faqs: [
      {
        question: 'Can I keep Intercom and add Minimoes?',
        answer: 'Yes. Many teams keep Intercom as their helpdesk. Minimoes becomes the face on the website, and it can open tickets in Intercom when a visitor needs a human.',
      },
      {
        question: 'How does pricing compare?',
        answer: `Intercom lists seats from $29 to $132 per month plus $0.99 per Fin outcome (public pricing, ${PRICES_CHECKED}). Minimoes has a free plan, then flat plans at $29, $149 and $590 per month with included credits.`,
      },
      {
        question: 'Is Minimoes a full helpdesk?',
        answer: 'No. It is the AI front line: a talking mascot that answers, captures leads, books and escalates. For ticket management it connects to your existing helpdesk.',
      },
    ],
    ctaHeadline: 'Give your support AI a face',
    ctaBody: 'Create your mascot free. It can escalate straight into Intercom.',
  },

  tidio: {
    headline: 'Minimoes vs Tidio: Upgrade the Chat Bubble to a Talking Mascot',
    subheadline:
      'Tidio pairs live chat with the Lyro AI add-on. Minimoes replaces the bubble with a 3D AI mascot that speaks, listens, sells and books.',
    switchReason:
      `Tidio is a solid live chat tool, and its Lyro AI is sold as an add-on priced by conversation volume (from $39/month for 50 conversations, ${PRICES_CHECKED}). Minimoes is AI-first. Your mascot answers from your content out loud, handles Shopify orders and Stripe billing questions, books on Calendly or Cal.com, and hands off to a human when needed.`,
    comparisonRows: [
      { feature: 'Visual presence', minimoes: '3D mascot with voice', competitor: 'Text chat bubble', winner: 'minimoes' },
      { feature: 'AI pricing', minimoes: 'Included credits per plan', competitor: 'Lyro add-on by conversations', winner: 'minimoes' },
      { feature: 'Shopify actions', minimoes: 'Products, orders, cart', competitor: 'Yes', winner: 'tie' },
      { feature: 'Human live chat inbox', minimoes: 'Via your helpdesk', competitor: 'Built-in', winner: 'competitor' },
      { feature: 'App & stream deploy', minimoes: 'React, RN, Flutter, OBS', competitor: 'Web & mobile chat', winner: 'minimoes' },
    ],
    competitorWeaknesses: [
      'AI is a separate add-on priced by conversation volume',
      'A text bubble does not stand out',
    ],
    minimoesStrengths: [
      'AI included in every plan, with a free tier',
      'A mascot with your brand look and voice',
      'Deploys on sites, apps and livestreams',
    ],
    stats: [
      { value: '$0', label: 'To start' },
      { value: '3', label: 'Channels: web, apps, streams' },
      { value: '3D', label: 'Mascot with a voice' },
    ],
    faqs: [
      {
        question: 'Is Minimoes a Tidio alternative for Shopify stores?',
        answer: 'Yes. Install it on your theme in minutes, and turn on the Shopify action so it can browse products, track orders and check cart status.',
      },
      {
        question: 'Does Minimoes have live chat with human agents?',
        answer: 'Minimoes is the AI front line. When a human is needed, it opens a ticket in your helpdesk or hands off live to Salesforce.',
      },
      {
        question: 'How does pricing compare?',
        answer: `Tidio plans start at $29/month, with Lyro AI as an add-on from $39/month (${PRICES_CHECKED}). Minimoes starts free, then $29/month with AI credits included.`,
      },
    ],
    ctaHeadline: 'Replace the bubble with a mascot',
    ctaBody: 'Create yours free. Live on your store in minutes.',
  },

  chatbase: {
    headline: 'Minimoes vs Chatbase: Same Idea, Now With a Face and a Voice',
    subheadline:
      'Chatbase trains a text chatbot on your content. Minimoes does that too, and puts it in a talking 3D mascot that acts.',
    switchReason:
      `Chatbase is a popular way to train a text chatbot on your docs, with plans from $32 to $400/month billed annually (${PRICES_CHECKED}). Minimoes covers the same knowledge-base use case at similar prices, then adds a 3D mascot with a voice, booking, Shopify and Stripe actions, and deployment to apps and livestreams.`,
    comparisonRows: [
      { feature: 'Train on your content', minimoes: 'Files, web, Q&A, Notion, Zendesk', competitor: 'Files, web, Q&A & more', winner: 'tie' },
      { feature: 'Visual presence', minimoes: '3D mascot with voice', competitor: 'Text chat widget', winner: 'minimoes' },
      { feature: 'Free plan', minimoes: '50 credits / month', competitor: '50 credits / month', winner: 'tie' },
      { feature: 'Entry paid plan', minimoes: '$29 / month', competitor: '$32 / month (annual)', winner: 'minimoes' },
      { feature: 'Livestream overlay', minimoes: 'Yes', competitor: 'No', winner: 'minimoes' },
    ],
    competitorWeaknesses: [
      'Text-only widget, so it looks like every other chatbot',
    ],
    minimoesStrengths: [
      'Same knowledge-base workflow, plus a talking 3D mascot',
      'Flags contradictions and missing content in your docs',
      'Deploys to sites, apps and livestreams',
    ],
    stats: [
      { value: '$29', label: 'Entry plan' },
      { value: '3D', label: 'Mascot with a voice' },
      { value: '3', label: 'Gap reports' },
    ],
    faqs: [
      {
        question: 'Is Minimoes a Chatbase alternative?',
        answer: 'Yes. You train it on your content the same way, and it answers through a 3D mascot that talks instead of a text box.',
      },
      {
        question: 'How do prices compare?',
        answer: `Both have a free plan with 50 credits per month. Chatbase paid plans start at $32/month billed annually (${PRICES_CHECKED}). Minimoes paid plans start at $29/month.`,
      },
      {
        question: 'Can I migrate my content?',
        answer: 'Add the same sources: your website URL, files and Q&A pairs. Training takes minutes.',
      },
    ],
    ctaHeadline: 'Give your chatbot a face',
    ctaBody: 'Create your mascot free with your existing content.',
  },

  botpress: {
    headline: 'Minimoes vs Botpress: Build Flows, or Ship a Mascot Today',
    subheadline:
      'Botpress is a powerful builder for developers who want to design every flow. Minimoes is the fastest way to put a talking AI mascot on your site, with no flows to build.',
    switchReason:
      'Botpress gives developers a visual flow builder and deep customization. Minimoes trades that for speed and presence. You design a mascot, add your content, turn on the actions you need (leads, booking, Shopify, Stripe, escalation, webhooks), and paste one snippet.',
    comparisonRows: [
      { feature: 'Setup', minimoes: 'No flows to build', competitor: 'Flow builder', winner: 'minimoes' },
      { feature: 'Visual presence', minimoes: '3D mascot with voice', competitor: 'Chat widget', winner: 'minimoes' },
      { feature: 'Custom logic', minimoes: 'Intent-triggered webhooks', competitor: 'Full flow & code control', winner: 'competitor' },
      { feature: 'Livestream overlay', minimoes: 'Yes', competitor: 'No', winner: 'minimoes' },
    ],
    competitorWeaknesses: [
      'Building and maintaining flows takes developer time',
    ],
    minimoesStrengths: [
      'Live in minutes with no flows to design',
      'A branded mascot with a voice',
    ],
    stats: [
      { value: '5 min', label: 'To go live' },
      { value: '0', label: 'Flows to build' },
      { value: '3D', label: 'Mascot with a voice' },
    ],
    faqs: [
      {
        question: 'When should I pick Botpress over Minimoes?',
        answer: 'When you need complex, developer-built conversation logic. Pick Minimoes when you want a talking, branded AI live today with actions built in.',
      },
      {
        question: 'Can Minimoes call my backend?',
        answer: 'Yes. Custom actions trigger any webhook (POST, GET or PATCH) with a custom payload when an intent is detected.',
      },
      {
        question: 'Is there a free plan?',
        answer: 'Yes, with 1 Twin and 50 credits per month.',
      },
    ],
    ctaHeadline: 'Skip the flow builder',
    ctaBody: 'Create your mascot free and go live today.',
  },

  // ─── Big-tech agents with a face: they validate the category ────────────────
  // Consumer assistants, not business widgets. Never claim they "cannot" be embedded:
  // say no public website-embedding option was found as of PRICES_CHECKED.

  'meta-muse': {
    headline: 'Minimoes vs Meta Muse (Jolly): Meta Gave Its Agent a Face. Give Your Brand One.',
    subheadline:
      "Jolly is the fuzzy, customizable mascot of Muse, Meta's personal AI agent. Minimoes brings the same idea to your business: a branded 3D mascot that talks to your customers on your own site, app or stream.",
    switchReason:
      `Muse launched on September 8, 2026 as a personal agent for consumers. It handles messages, bookings, forms and purchases for its user, through its app and Meta's platforms. Its Jolly mascot exists to make that agent feel approachable. It is a free app with paid tiers at $20 and $100 a month, launched in the US first (${PRICES_CHECKED}). Muse works for the person using it. Minimoes works for your brand. Your own mascot greets your visitors, answers from your content, captures leads, books meetings and hands off to your team, on your website, in your app or on your livestream.`,
    comparisonRows: [
      { feature: 'Who it works for', minimoes: 'Your business & its visitors', competitor: 'The individual user', winner: 'tie' },
      { feature: 'Where it lives', minimoes: 'Your site, your app, your stream', competitor: 'Muse app & Meta platforms', winner: 'minimoes' },
      { feature: 'Character', minimoes: 'Your own branded 3D mascot', competitor: 'Jolly, customizable by the user', winner: 'minimoes' },
      { feature: 'Trained on your business content', minimoes: 'Files, web, Q&A, Notion, Zendesk', competitor: 'Personal assistant, not a business knowledge base', winner: 'minimoes' },
      { feature: 'Leads, booking & escalation for your team', minimoes: 'Built in', competitor: 'Not its purpose', winner: 'minimoes' },
      { feature: 'Personal errands (email, travel, shopping)', minimoes: 'No', competitor: 'Yes', winner: 'competitor' },
    ],
    competitorWeaknesses: [
      'Built as a personal assistant for consumers, not a face for your brand',
      'No public option found to put Muse or Jolly on your own website',
    ],
    minimoesStrengths: [
      'Your mascot, your look, your voice, on your channels',
      'Answers from your content and acts for your business',
      'Free plan, live in minutes',
    ],
    stats: [
      { value: 'Sep 2026', label: 'Meta gave its agent a mascot' },
      { value: 'Yours', label: 'Mascot, on your own site' },
      { value: '$0', label: 'To start' },
    ],
    faqs: [
      {
        question: 'Can I put Meta Muse or Jolly on my business website?',
        answer: `We found no public option to do that (${PRICES_CHECKED}). Muse is a personal agent people use through Meta's apps. If you want a mascot like Jolly that talks to your own customers, that is what Minimoes is built for.`,
      },
      {
        question: 'Why does it matter that Meta gave Muse a mascot?',
        answer: 'Meta and OpenAI both launched mascots for their agents in September 2026, to make AI feel approachable and lower the barrier to using it. Visitors are learning to expect AI with a face, and your website can meet that expectation.',
      },
      {
        question: 'Is Minimoes a Muse competitor?',
        answer: 'Not really. Muse helps individuals with their own tasks. Minimoes gives businesses a branded AI mascot that serves their customers. They answer different needs.',
      },
    ],
    ctaHeadline: 'Meta has Jolly. What does your brand have?',
    ctaBody: 'Create your own mascot free. Live on your site in minutes.',
  },

  'openai-dots': {
    headline: 'Minimoes vs OpenAI Dots: Always-On Agents With a Face, for Your Customers',
    subheadline:
      "OpenAI's dots are colorful, always-on agents that work for ChatGPT subscribers. Minimoes gives your brand its own always-on mascot that works for your visitors.",
    switchReason:
      `OpenAI introduced dots at DevDay on September 29, 2026. They are always-on agents with their own cloud computer, connected to thousands of apps and reachable from ChatGPT, Slack, Teams or by phone. Each one is a colorful character. Reported availability is limited to ChatGPT's top Pro and Business Premium plans (${PRICES_CHECKED}). Dots work for the subscriber and their team. Minimoes is customer-facing: a 3D mascot on your website, app or stream that answers your visitors, captures leads, books meetings and escalates to your team.`,
    comparisonRows: [
      { feature: 'Who it works for', minimoes: 'Your visitors & customers', competitor: 'The ChatGPT subscriber & team', winner: 'tie' },
      { feature: 'Where it lives', minimoes: 'Your site, your app, your stream', competitor: 'ChatGPT, Slack, Teams, phone', winner: 'tie' },
      { feature: 'Character', minimoes: 'Your own branded 3D mascot with a voice', competitor: 'A dot character', winner: 'minimoes' },
      { feature: 'Customer-facing website widget', minimoes: 'One snippet', competitor: 'Not its purpose', winner: 'minimoes' },
      { feature: 'Internal work across thousands of apps', minimoes: 'No', competitor: 'Yes', winner: 'competitor' },
      { feature: 'Starting price', minimoes: 'Free, then $29 / month', competitor: 'Top ChatGPT Pro or Business Premium plan', winner: 'minimoes' },
    ],
    competitorWeaknesses: [
      'Built to work for subscribers and their teams, not to face your customers',
      'Reported availability limited to the most expensive ChatGPT plans',
    ],
    minimoesStrengths: [
      'Customer-facing by design: greets, answers, sells, books',
      'Your own look and voice, not a shared character',
      'Free plan, flat monthly pricing',
    ],
    stats: [
      { value: 'Sep 2026', label: 'OpenAI gave its agents a face' },
      { value: '24/7', label: 'Your mascot, for your customers' },
      { value: '$0', label: 'To start' },
    ],
    faqs: [
      {
        question: 'Can I embed an OpenAI dot on my website?',
        answer: `We found no public option for that (${PRICES_CHECKED}). Dots are agents that work for ChatGPT subscribers inside ChatGPT, Slack, Teams or by phone. For a mascot that talks to your website visitors, use Minimoes.`,
      },
      {
        question: 'Can I use both?',
        answer: 'Yes. A dot can work for your team internally, while your Minimoes mascot works for your customers on your site, app or stream.',
      },
      {
        question: 'Why give a customer-facing AI a face?',
        answer: 'The same reason OpenAI and Meta did. A character makes AI feel approachable and easier to trust and talk to. Research on chatbots links human-like presence with trust and purchase intent.',
      },
    ],
    ctaHeadline: 'OpenAI has dots. Give your customers a mascot.',
    ctaBody: 'Create yours free. Live on your site in minutes.',
  },

  grok: {
    headline: 'Minimoes vs Grok Companions: 3D Characters, Built for Business',
    subheadline:
      'Grok Companions showed how much people enjoy talking to a 3D character. Minimoes puts a talking 3D mascot to work for your brand, on your website, app or stream.',
    switchReason:
      `xAI's Grok Companions (Ani, Mika, Valentine, Rudi) were 3D animated characters with real-time voice and lip-sync, aimed at personal companionship inside the Grok app (SuperGrok from $30/month). In 2026, xAI retired the companion mode from Grok. The characters continue in a separate 18+ app called Animates (${PRICES_CHECKED}). Minimoes takes the part that worked, a 3D character with a voice that people want to talk to, and makes it a business tool. Your mascot answers from your content, captures leads, books meetings, handles Shopify and Stripe questions and escalates to your team.`,
    comparisonRows: [
      { feature: 'Purpose', minimoes: 'Business front line', competitor: 'Personal companionship', winner: 'tie' },
      { feature: 'Your own branded character', minimoes: 'Yes, 3D studio + voice', competitor: 'Fixed characters', winner: 'minimoes' },
      { feature: 'Embeddable on your site or app', minimoes: 'One snippet or SDK', competitor: 'No public option found', winner: 'minimoes' },
      { feature: 'Trained on your business content', minimoes: 'Yes', competitor: 'No', winner: 'minimoes' },
      { feature: 'Leads, booking & escalation', minimoes: 'Built in', competitor: 'Not its purpose', winner: 'minimoes' },
      { feature: 'Availability', minimoes: 'Live', competitor: 'Retired from Grok in 2026', winner: 'minimoes' },
    ],
    competitorWeaknesses: [
      'Consumer companions, not a tool for businesses',
      'Companion mode retired from the Grok app in 2026',
    ],
    minimoesStrengths: [
      'A 3D character with a voice, designed for your brand',
      'Brand-safe: answers only from your content, escalates when needed',
      'Deploys on websites, apps and livestreams',
    ],
    stats: [
      { value: '3D', label: 'Character with a voice' },
      { value: '100%', label: 'Answers from your content' },
      { value: '$0', label: 'To start' },
    ],
    faqs: [
      {
        question: 'Can I use Ani or another Grok companion on my website?',
        answer: `We found no public option for businesses, and xAI retired the companion mode from Grok in 2026 (${PRICES_CHECKED}). With Minimoes you design your own character and put it on your site in minutes.`,
      },
      {
        question: 'Is Minimoes a companion app?',
        answer: "No. Minimoes is a brand mascot for businesses. It talks to your visitors about your products and services, from your own content, and stays within the scope you set.",
      },
      {
        question: 'Does it have voice and lip-sync like Grok companions?',
        answer: 'Yes. Your mascot speaks out loud with real-time lip-sync, and visitors can talk back with their microphone.',
      },
    ],
    ctaHeadline: 'Put a 3D character to work for your brand',
    ctaBody: 'Create your mascot free. Live in minutes.',
  },

  // ─── Video & interactive avatars ────────────────────────────────────────────

  synthesia: {
    headline: 'Minimoes vs Synthesia: Live Conversation vs Produced Video',
    subheadline:
      'Synthesia is built to produce avatar videos. Minimoes holds live conversations: your mascot listens, answers and acts in real time.',
    switchReason:
      'Synthesia is a video production tool: you script, render and publish. Minimoes is live. Every visitor asks something different, and your mascot answers from your knowledge base, then captures the lead or books the meeting. If you need finished videos, use Synthesia. If you need your website to talk back, use Minimoes.',
    comparisonRows: [
      { feature: 'Main use', minimoes: 'Live conversations', competitor: 'Video production', winner: 'tie' },
      { feature: 'Answers visitor questions live', minimoes: 'Yes', competitor: 'Not the core product', winner: 'minimoes' },
      { feature: 'Lead capture & booking', minimoes: 'Built in', competitor: 'Not the core product', winner: 'minimoes' },
      { feature: 'Produce finished videos', minimoes: 'No', competitor: 'Yes', winner: 'competitor' },
      { feature: 'Style', minimoes: 'Stylized 3D mascot', competitor: 'Realistic human presenters', winner: 'tie' },
    ],
    competitorWeaknesses: [
      'A produced video cannot answer what each visitor actually asks',
    ],
    minimoesStrengths: [
      'Answers each visitor live from your content',
      'Captures leads and books meetings',
      'Free plan to try it',
    ],
    stats: [
      { value: 'Live', label: 'vs. produced video' },
      { value: '24/7', label: 'Conversations' },
      { value: '$0', label: 'To start' },
    ],
    faqs: [
      {
        question: 'What is the main difference between Minimoes and Synthesia?',
        answer: 'Synthesia produces videos from a script. Minimoes runs live two-way conversations with a 3D mascot that answers from your content and takes actions.',
      },
      {
        question: 'Can I use both?',
        answer: 'Yes. Use Synthesia for training or marketing videos, and Minimoes for live conversations on your site, app or stream.',
      },
      {
        question: 'Why a stylized mascot instead of a realistic human?',
        answer: 'Research on e-commerce chatbots suggests that a cartoon-like look combined with warm behaviour can lift purchase intent, while near-human faces risk the "uncanny valley". A mascot is also unmistakably your brand.',
      },
    ],
    ctaHeadline: 'If your website needs to talk back, start here',
    ctaBody: 'Create your mascot free. No video production required.',
  },

  heygen: {
    headline: 'Minimoes vs HeyGen LiveAvatar: Flat Plans vs Per-Minute Streaming',
    subheadline:
      'HeyGen LiveAvatar streams a realistic talking head and bills per streamed minute. Minimoes is a stylized 3D mascot on flat monthly plans, with actions built in.',
    switchReason:
      `HeyGen LiveAvatar is a developer-first video face: 1 credit = $0.10, and one streamed minute costs 1–2 credits depending on mode (${PRICES_CHECKED}). Costs grow with every minute visitors talk. Minimoes runs on flat plans with included credits, and comes with a no-code widget, a knowledge base, lead capture, booking and escalation.`,
    comparisonRows: [
      { feature: 'Pricing model', minimoes: 'Flat monthly plans', competitor: 'Credits per streamed minute', winner: 'minimoes' },
      { feature: 'Style', minimoes: 'Stylized 3D mascot', competitor: 'Realistic video avatar', winner: 'tie' },
      { feature: 'No-code website widget', minimoes: 'Yes', competitor: 'Developer-first API & SDK', winner: 'minimoes' },
      { feature: 'Actions (leads, booking, Shopify, Stripe)', minimoes: 'Built in', competitor: 'Build it yourself', winner: 'minimoes' },
      { feature: 'Video generation', minimoes: 'No', competitor: 'Yes', winner: 'competitor' },
    ],
    competitorWeaknesses: [
      'Per-minute billing makes costs grow with engagement',
      'Business logic is yours to build',
    ],
    minimoesStrengths: [
      'Predictable monthly plans',
      'Knowledge base and actions included',
      'No-code install on any site',
    ],
    stats: [
      { value: 'Flat', label: 'vs. per-minute billing' },
      { value: '0', label: 'Code to install' },
      { value: '$0', label: 'To start' },
    ],
    faqs: [
      {
        question: 'How does pricing compare?',
        answer: `HeyGen LiveAvatar bills credits per streamed minute ($0.10 per credit, 1–2 credits per minute, plans from free to $475/month, ${PRICES_CHECKED}). Minimoes plans are flat at $29, $149 or $590 per month, and one credit is one AI response.`,
      },
      {
        question: 'Which one looks more realistic?',
        answer: 'HeyGen, by design. Minimoes deliberately uses a stylized mascot: it is unmistakably your brand and avoids the uncanny valley of near-human faces.',
      },
      {
        question: 'Do I need a developer for Minimoes?',
        answer: 'No. Paste one snippet or use the WordPress plugin. There are SDKs for React, React Native and Flutter if you want to go further.',
      },
    ],
    ctaHeadline: 'Predictable plans, a mascot that acts',
    ctaBody: 'Create yours free. No per-minute bills.',
  },

  'heygen-interactive': {
    headline: 'Minimoes vs HeyGen Interactive Avatar: Built to Ship, Not Just Demo',
    subheadline:
      'A no-code talking mascot with a knowledge base and actions, on flat monthly plans.',
    switchReason:
      'HeyGen interactive avatars are developer-first and billed by streamed minute. Minimoes is a ready-to-install widget with flat plans, a knowledge base, lead capture, booking and escalation built in.',
    comparisonRows: [],
    competitorWeaknesses: [],
    minimoesStrengths: [],
    stats: [],
    faqs: [],
    ctaHeadline: 'Start free with Minimoes',
    ctaBody: 'Flat plans, a mascot that acts.',
  },

  tavus: {
    headline: 'Minimoes vs Tavus: A Brand Mascot vs a Video Human',
    subheadline:
      'Tavus builds realistic conversational video humans, billed by the minute. Minimoes gives your brand a stylized 3D mascot on flat plans, with a free tier.',
    switchReason:
      `Tavus plans run from $59/month (100 minutes) to $397/month (1,250 minutes), with overage around $0.37 per minute (${PRICES_CHECKED}). That works well for realistic video conversations at controlled volume. Minimoes is built for always-on website traffic. Flat plans, a free tier, and a mascot that captures leads, books and escalates without extra development.`,
    comparisonRows: [
      { feature: 'Style', minimoes: 'Stylized 3D mascot', competitor: 'Realistic video human', winner: 'tie' },
      { feature: 'Pricing model', minimoes: 'Flat monthly plans', competitor: 'Minutes included, then per minute', winner: 'minimoes' },
      { feature: 'Entry price', minimoes: '$0, then $29 / month', competitor: '$59 / month', winner: 'minimoes' },
      { feature: 'No-code website widget', minimoes: 'Yes', competitor: 'Developer-first', winner: 'minimoes' },
      { feature: 'Realism of the face', minimoes: 'Stylized by design', competitor: 'Photo-realistic', winner: 'competitor' },
    ],
    competitorWeaknesses: [
      'Per-minute model makes always-on website traffic costly',
    ],
    minimoesStrengths: [
      'Free plan and flat pricing',
      'Actions and knowledge base included',
    ],
    stats: [
      { value: '$0', label: 'To start' },
      { value: 'Flat', label: 'vs. per-minute' },
      { value: '3D', label: 'Brand mascot' },
    ],
    faqs: [
      {
        question: 'How does pricing compare?',
        answer: `Tavus lists $59 to $397 per month with included minutes and about $0.37 per extra minute (${PRICES_CHECKED}). Minimoes is free to start, then $29, $149 or $590 per month.`,
      },
      {
        question: 'Why choose a mascot over a realistic human?',
        answer: 'A mascot is unmistakably your brand, never pretends to be a person, and avoids the discomfort of near-human faces. Research suggests a cartoon-like look with warm behaviour can lift purchase intent.',
      },
      {
        question: 'Do I need developers?',
        answer: 'No. One snippet, or the WordPress plugin. SDKs are available if you want them.',
      },
    ],
    ctaHeadline: 'Give your brand a face of its own',
    ctaBody: 'Create your mascot free. No per-minute bills.',
  },

  'd-id': {
    headline: 'Minimoes vs D-ID: Talking Photos vs a Mascot That Acts',
    subheadline:
      'D-ID animates realistic faces for video and agents. Minimoes is a stylized 3D mascot built for websites, with booking, Shopify, Stripe and escalation included.',
    switchReason:
      'D-ID is known for turning photos into talking faces, with affordable creator plans. Minimoes is focused on one job: a branded AI front line for your website, app or stream. It answers from your content, captures leads, books meetings and hands off to humans.',
    comparisonRows: [
      { feature: 'Style', minimoes: 'Stylized 3D mascot', competitor: 'Realistic animated faces', winner: 'tie' },
      { feature: 'Website actions', minimoes: 'Leads, booking, Shopify, Stripe', competitor: 'Check their plans', winner: 'minimoes' },
      { feature: 'Livestream overlay', minimoes: 'OBS: Twitch, YouTube, Kick', competitor: 'No', winner: 'minimoes' },
      { feature: 'Video generation', minimoes: 'No', competitor: 'Yes', winner: 'competitor' },
    ],
    competitorWeaknesses: [],
    minimoesStrengths: [
      'Built for always-on website conversations',
      'Actions and escalation included',
    ],
    stats: [
      { value: '$0', label: 'To start' },
      { value: '3', label: 'Channels: web, apps, streams' },
      { value: '7', label: 'Helpdesks for escalation' },
    ],
    faqs: [
      {
        question: 'What is the difference between D-ID and Minimoes?',
        answer: 'D-ID focuses on realistic talking faces for video and agents. Minimoes focuses on a branded 3D mascot that runs your website front line, from answering and selling to booking and escalating.',
      },
      {
        question: 'Can Minimoes make videos?',
        answer: 'No. It is built for live conversations, not video production.',
      },
      {
        question: 'Is there a free plan?',
        answer: 'Yes, with 1 Twin and 50 credits per month, no card needed.',
      },
    ],
    ctaHeadline: 'Put a mascot that acts on your site',
    ctaBody: 'Create yours free in minutes.',
  },

  'did-studio': {
    headline: 'Minimoes vs D-ID Studio: Produce Clips, or Hold Conversations',
    subheadline:
      'D-ID Studio creates talking-head videos. Minimoes runs live conversations on your website with a 3D mascot.',
    switchReason:
      'If you need talking-head clips for content, D-ID Studio is built for it. If you need your website to greet, answer, sell and book, Minimoes is built for that.',
    comparisonRows: [
      { feature: 'Main use', minimoes: 'Live website conversations', competitor: 'Talking-head videos', winner: 'tie' },
      { feature: 'Lead capture & booking', minimoes: 'Built in', competitor: 'Not the core product', winner: 'minimoes' },
      { feature: 'Video clips', minimoes: 'No', competitor: 'Yes', winner: 'competitor' },
    ],
    competitorWeaknesses: [],
    minimoesStrengths: ['Live answers from your content', 'Actions included'],
    stats: [
      { value: 'Live', label: 'vs. produced clips' },
      { value: '24/7', label: 'Conversations' },
      { value: '$0', label: 'To start' },
    ],
    faqs: [
      { question: 'Should I use D-ID Studio or Minimoes?', answer: 'Use D-ID Studio to produce videos. Use Minimoes when you want a mascot that talks with your visitors live.' },
      { question: 'Can I try Minimoes for free?', answer: 'Yes. The Free plan includes 1 Twin and 50 credits per month.' },
      { question: 'Where can I deploy Minimoes?', answer: 'Any website, React, React Native and Flutter apps, and OBS for livestreams.' },
    ],
    ctaHeadline: 'Let your website hold the conversation',
    ctaBody: 'Create your mascot free.',
  },

  akool: {
    headline: 'Minimoes vs Akool: Creative Avatar Suite vs a Website Front Line',
    subheadline:
      'Akool offers a broad suite of AI video and avatar tools. Minimoes does one thing: a talking 3D mascot that runs conversations on your site, app or stream.',
    switchReason:
      'Creative suites are great for producing content. Minimoes is built for conversations that convert. It answers from your knowledge base, captures leads, books meetings, handles Shopify and Stripe questions and escalates to humans, on flat monthly plans.',
    comparisonRows: [
      { feature: 'Focus', minimoes: 'Website conversations', competitor: 'Creative AI suite', winner: 'tie' },
      { feature: 'Lead capture & booking', minimoes: 'Built in', competitor: 'Not the core product', winner: 'minimoes' },
      { feature: 'Content production tools', minimoes: 'No', competitor: 'Yes', winner: 'competitor' },
    ],
    competitorWeaknesses: [],
    minimoesStrengths: ['Focused on conversion', 'Free plan and flat pricing'],
    stats: [
      { value: '$0', label: 'To start' },
      { value: '24/7', label: 'Conversations' },
      { value: '5 min', label: 'To go live' },
    ],
    faqs: [
      { question: 'Is Minimoes an Akool alternative?', answer: 'For live website conversations, yes. For producing videos and creative content, Akool covers more.' },
      { question: 'Can it sell and book?', answer: 'Yes. It captures leads, books on Calendly or Cal.com, and handles Shopify and Stripe questions.' },
      { question: 'Is there a free plan?', answer: 'Yes, 1 Twin and 50 credits per month.' },
    ],
    ctaHeadline: 'Put a mascot to work on your site',
    ctaBody: 'Create yours free.',
  },

  convai: {
    headline: 'Minimoes vs Convai: Game Characters vs a Website Mascot',
    subheadline:
      'Convai powers conversational characters for games and virtual worlds. Minimoes puts a conversational 3D mascot on your website, app or stream, with no game engine.',
    switchReason:
      'Convai is built for developers creating characters in game engines and virtual worlds. Minimoes is built for businesses. You get a no-code widget, a knowledge base, lead capture, booking, Shopify, Stripe and escalation.',
    comparisonRows: [
      { feature: 'Target', minimoes: 'Websites, apps, streams', competitor: 'Games & virtual worlds', winner: 'tie' },
      { feature: 'No-code website widget', minimoes: 'Yes', competitor: 'Developer tools', winner: 'minimoes' },
      { feature: 'Business actions', minimoes: 'Leads, booking, Shopify, Stripe', competitor: 'Not the core product', winner: 'minimoes' },
      { feature: 'Game engine integrations', minimoes: 'No', competitor: 'Yes', winner: 'competitor' },
    ],
    competitorWeaknesses: [],
    minimoesStrengths: ['No-code install', 'Business actions built in'],
    stats: [
      { value: '0', label: 'Game engines needed' },
      { value: '5 min', label: 'To go live' },
      { value: '$0', label: 'To start' },
    ],
    faqs: [
      { question: 'Do I need Unity or Unreal for Minimoes?', answer: 'No. It runs in the browser through one snippet, or in your app through the React, React Native or Flutter SDKs.' },
      { question: 'Can it be used on a stream?', answer: 'Yes. Add it as an OBS browser source with a transparent background and connect Twitch, YouTube or Kick chat.' },
      { question: 'Is there a free plan?', answer: 'Yes, 1 Twin and 50 credits per month.' },
    ],
    ctaHeadline: 'A conversational character, minus the game engine',
    ctaBody: 'Create your mascot free.',
  },

  colossyan: {
    headline: 'Minimoes vs Colossyan: Training Videos vs Live Answers',
    subheadline:
      'Colossyan produces avatar videos for learning teams. Minimoes answers learners and customers live, with a 3D mascot trained on your content.',
    switchReason:
      'Video is great for delivering a course. But learners have questions the video cannot answer. Minimoes sits next to your content and answers them live, out loud, from your own materials.',
    comparisonRows: [
      { feature: 'Main use', minimoes: 'Live Q&A', competitor: 'Training videos', winner: 'tie' },
      { feature: 'Answers learner questions', minimoes: 'Yes, live', competitor: 'Not the core product', winner: 'minimoes' },
      { feature: 'Video production', minimoes: 'No', competitor: 'Yes', winner: 'competitor' },
    ],
    competitorWeaknesses: [],
    minimoesStrengths: ['Answers questions live from your materials', 'Gap reports on your content'],
    stats: [
      { value: 'Live', label: 'Answers' },
      { value: '24/7', label: 'Learner support' },
      { value: '$0', label: 'To start' },
    ],
    faqs: [
      { question: 'Can I use Colossyan and Minimoes together?', answer: 'Yes. Produce lessons with Colossyan, and put a Minimoes mascot on the course page to answer questions.' },
      { question: 'Can it be trained on my course?', answer: 'Yes. Upload files, crawl pages or connect Notion.' },
      { question: 'Is there a free plan?', answer: 'Yes, 1 Twin and 50 credits per month.' },
    ],
    ctaHeadline: 'Answer the questions your videos cannot',
    ctaBody: 'Create your mascot free.',
  },
};

